import { StateCreator } from 'zustand';
import { type ModuleId, isValidModule, MODULE_AGENT_MAP, DEFAULT_PROJECT_ID } from '@/core/constants';
import type { ProjectMetadata } from '@/services/dashboard/DashboardService';
import { logger } from '@/utils/logger';

// Migration: Support old indii_ localStorage keys from before rebranding to indii.music
// This function is called once on app startup to preserve existing user preferences
function migrateStorageKeys(): void {
    if (typeof window === 'undefined') return;

    const keysToMigrate = [
        'indii_entryAssistantDismissed',
        'indii_sidebarOpen',
        'indii_commandBarPosition',
        'indii_first_run_tips',
        'indii_tour_completed_v1',
    ];

    const migrationVersion = 'indii_migration_v1';
    const hasMigrated = localStorage.getItem(migrationVersion) === 'true';

    if (hasMigrated) return; // Only run once per browser

    try {
        for (const oldKey of keysToMigrate) {
            const value = localStorage.getItem(oldKey);
            if (value !== null) {
                const newKey = oldKey.replace('indii_', 'indii_');
                localStorage.setItem(newKey, value);
                localStorage.removeItem(oldKey);
                logger.debug(`[AppSlice] Migrated ${oldKey} → ${newKey}`);
            }
        }
        localStorage.setItem(migrationVersion, 'true');
    } catch (err) {
        logger.error('[AppSlice] Storage migration failed:', err);
    }
}

// Run migration on module load
if (typeof window !== 'undefined') {
    migrateStorageKeys();
}

// Helper to get initial module from URL
const getInitialModule = (): ModuleId => {
    if (typeof window === 'undefined') return 'dashboard';
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    if (firstSegment && isValidModule(firstSegment)) {
        return firstSegment;
    }
    return 'dashboard';
};

export interface Project {
    id: string;
    name: string;
    type: AppSlice['currentModule'];
    date?: number;
    lastModified?: number;
    orgId: string;
    status?: 'active' | 'paused' | 'archived';
    thumbnail?: string;
    assetCount?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: Record<string, any>;
}

export interface AppSlice {
    currentModule: ModuleId;
    currentProjectId: string;
    projects: ProjectMetadata[]; // Changed from Project[] to enforce UI type
    setModule: (module: AppSlice['currentModule'], options?: { tab?: string }) => Promise<void>;
    // ISSUE-1375: return to the module visited before the current one (the
    // store already tracks unique visited modules in _navigationHistory).
    goBackModule: () => Promise<void>;
    setProject: (id: string) => void;
    addProject: (project: ProjectMetadata) => void; // Changed parameter type
    loadProjects: () => Promise<void>;
    createNewProject: (name: string, type: Project['type'], orgId: string) => Promise<string>;
     
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateProjectMetadata: (projectId: string, metadata: Record<string, any>) => Promise<void>;
    updateProjectName: (projectId: string, name: string) => Promise<void>;
    setProjectStatus: (projectId: string, status: 'active' | 'paused' | 'archived') => Promise<void>;
    deleteProject: (projectId: string) => Promise<void>;
    pendingPrompt: string | null;
    setPendingPrompt: (prompt: string | null) => void;
    apiKeyError: boolean;
    setApiKeyError: (error: boolean) => void;
    isSidebarOpen: boolean;
    isRightPanelOpen: boolean;
    rightPanelTab: 'context' | 'assets' | 'agent' | 'artifacts' | 'approvals' | 'knowledge';
    toggleSidebar: () => void;
    toggleRightPanel: () => void;
    setRightPanelTab: (tab: 'context' | 'assets' | 'agent' | 'artifacts' | 'approvals' | 'knowledge') => void;
    isCommandMenuOpen: boolean;
    setCommandMenuOpen: (open: boolean) => void;
    isSettingsOpen: boolean;
    setSettingsOpen: (open: boolean) => void;
    isQuickNotesOpen: boolean;
    setQuickNotesOpen: (open: boolean) => void;
    hasUnsavedChanges: boolean;
    setHasUnsavedChanges: (hasUnsaved: boolean) => void;
    isEntryAssistantDismissed: boolean;
    setEntryAssistantDismissed: (dismissed: boolean) => void;
    pendingCostWarning: { estimatedCost: number; reason: string; resolve: (approved: boolean) => void } | null;
    setPendingCostWarning: (warning: AppSlice['pendingCostWarning']) => void;
    /** @internal Debounce tracker for toggleSidebar */
    _lastSidebarToggle?: number;
    /** @internal Debounce tracker for toggleRightPanel */
    _lastRightPanelToggle?: number;
    /** @internal Navigation history stack for back button tracking (ISSUE-043) */
    _navigationHistory?: ModuleId[];
    /** @internal Debounce tracker for rapid module switches (ISSUE-043) */
    _lastModuleSwitch?: number;
    bugReportDialog?: { isOpen: boolean; type: 'bug' | 'feature'; prefilledError?: string; prefilledModule?: string };
    setBugReportDialog: (dialog: AppSlice['bugReportDialog']) => void;
    rightPanelWidth: number;
    setRightPanelWidth: (width: number) => void;
}

export const createAppSlice: StateCreator<AppSlice> = (set, get) => ({
    currentModule: getInitialModule(),
    currentProjectId: DEFAULT_PROJECT_ID,
    projects: [],
    hasUnsavedChanges: false,
    pendingCostWarning: null,
    bugReportDialog: { isOpen: false, type: 'bug' },
    rightPanelWidth: typeof window !== 'undefined' ? Math.max(320, Math.min(800, parseInt(localStorage.getItem('indii_rightPanelWidth') || '320', 10) || 320)) : 320,
    setRightPanelWidth: (width) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('indii_rightPanelWidth', width.toString());
        }
        set({ rightPanelWidth: width });
    },
    setPendingCostWarning: (warning) => set({ pendingCostWarning: warning }),
    setBugReportDialog: (dialog) => set({ bugReportDialog: dialog }),
    setHasUnsavedChanges: (hasUnsaved) => set({ hasUnsavedChanges: hasUnsaved }),
    isEntryAssistantDismissed: typeof window !== 'undefined' ? localStorage.getItem('indii_entryAssistantDismissed') === 'true' : false,
    setEntryAssistantDismissed: (dismissed) => {
        if (typeof window !== 'undefined') {
            if (dismissed) {
                localStorage.setItem('indii_entryAssistantDismissed', 'true');
            } else {
                localStorage.removeItem('indii_entryAssistantDismissed');
            }
        }
        set({ isEntryAssistantDismissed: dismissed });
    },
    isSettingsOpen: false,
    setSettingsOpen: (open) => set({ isSettingsOpen: open }),
    isQuickNotesOpen: false,
    setQuickNotesOpen: (open) => set({ isQuickNotesOpen: open }),
    setModule: async (rawModule, options) => {
        const module = rawModule;
        const tab = options?.tab;

        const state = get();
        const now = Date.now();

        // ISSUE-043: Debounce rapid navigation clicks (100ms min between switches)
        // Prevents history stack from being overwritten by double/triple-clicks
        if (state._lastModuleSwitch && now - state._lastModuleSwitch < 100) {
            logger.debug(`[AppSlice] Navigation debounced (${now - state._lastModuleSwitch}ms since last switch)`);
            return;
        }

        if (state.hasUnsavedChanges && state.currentModule !== module) {
            // Surface a proper React modal instead of a blocking browser dialog.
            // The UnsavedChangesModal listens to pendingCostWarning and resolves it.
            const approved = await new Promise<boolean>((resolve) => {
                set({
                    pendingCostWarning: {
                        estimatedCost: 0,
                        reason: 'You have unsaved changes that will be lost. Are you sure you want to leave this page?',
                        resolve,
                    },
                });
            });
            if (!approved) {
                return;
            }
            set({ hasUnsavedChanges: false });
        }

        const outgoingModule = state.currentModule;

        // Track navigation history for proper Back button behavior
        const history = state._navigationHistory ? [...state._navigationHistory] : [state.currentModule];
        const shouldAddToHistory = state.currentModule !== module && !history.includes(module);
        if (shouldAddToHistory && history[history.length - 1] !== module) {
            history.push(module);
        }

        // Aggressively tear down listeners from previous modules
        // This requires dynamic import of store to avoid circular dependency
        import('@/core/store').then(({ useStore }) => {
            const store = useStore.getState();

            // Set tab if requested
            if (tab) {
                if (module === 'distribution') {
                    store.setDistributionTab(tab);
                } else if (module === 'finance') {
                    store.setFinanceTab(tab);
                }
            }
            
            // Auto-align the active agent for the new module
            const targetAgent = MODULE_AGENT_MAP[module] || 'generalist';
            store.setDirectTargetAgentId(targetAgent);

            // Only clear if actually switching modules
            if (outgoingModule !== module) {
                // Clean up Firestore subscriptions for the module we're leaving
                // to prevent INTERNAL ASSERTION FAILED errors during rapid navigation
                const prefixes: Partial<Record<string, string>> = {
                    creative: 'creative_',
                    publishing: 'publishing_',
                    finance: 'finance_',
                    memory: 'memory_',
                    publicist: 'publicist_',
                    distribution: 'distribution_',
                    merch: 'merch_',
                };
                const prefix = prefixes[outgoingModule];
                if (prefix) {
                    store.clearSubscriptionsByPrefix(prefix);
                }
            }
        }).catch(err => logger.error('[AppSlice] Failed to cleanup subscriptions or align agent:', err));

        set({
            currentModule: module,
            _navigationHistory: history,
            _lastModuleSwitch: now,
        });
    },
    // ISSUE-1375: module-level Back. _navigationHistory holds the unique
    // modules visited in order; the entry before the current one is the page
    // the user came from. setModule won't re-push it (dedupe by includes).
    goBackModule: async () => {
        const state = get();
        const history = state._navigationHistory ?? [state.currentModule];
        const index = history.lastIndexOf(state.currentModule);
        if (index > 0) {
            await state.setModule(history[index - 1]);
        }
    },
    setProject: (id) => {
        // ISSUE-1395 (audit): the creative board's canvasImages is a global
        // array whose projectId field was never filtered or reset — after a
        // project switch the previous project's layers stayed visible and
        // editable, and Crop/Flatten could composite or delete other
        // projects' images. Board state is per-project: clear it on switch.
        const prevProjectId = get().currentProjectId;
        if (prevProjectId && prevProjectId !== id) {
            const current = get() as any;
            if (typeof current.resetProjectCanvas === 'function') {
                current.resetProjectCanvas();
            }
            if (Array.isArray(current.canvasImages) && current.canvasImages.length > 0) {
                set({
                    currentProjectId: id,
                    canvasImages: [],
                    selectedCanvasImageId: null,
                    failedVariationBatch: null,
                } as any);
            } else {
                set({ currentProjectId: id });
            }
        } else {
            set({ currentProjectId: id });
        }
        const state = get() as any;
        
        // Only switch session if we have agent slice loaded
        if (state.sessions && state.setActiveSession) {
            const sessions = Object.values(state.sessions) as any[];
            const currentActive = sessions.find(s => s.id === state.activeSessionId);
            
            // If current session doesn't belong to the new project
            if (currentActive && currentActive.projectId !== id) {
                // Find the most recent session for this project
                const recentForProject = sessions
                    .filter(s => s.projectId === id && !s.namespace && !s.isArchived)
                    .sort((a, b) => b.updatedAt - a.updatedAt)[0];
                    
                if (recentForProject) {
                    state.setActiveSession(recentForProject.id);
                } else if (state.createSession) {
                    const newId = state.createSession('New Conversation', undefined, undefined, id);
                    state.setActiveSession(newId);
                }
            }
        }
    },
    addProject: (project) => set((state) => ({ projects: [project, ...state.projects] })),
    loadProjects: async () => {
        const { ProjectService } = await import('@/services/ProjectService');
        const { OrganizationService } = await import('@/services/OrganizationService');
        const { projectsToMetadata } = await import('@/services/dashboard/projectTypeUtils');
        const orgId = OrganizationService.getCurrentOrgId();
        if (orgId) {
            const firestoreProjects = await ProjectService.getProjectsForOrg(orgId);
            // Convert Project[] to ProjectMetadata[] at the boundary
            const projects = projectsToMetadata(firestoreProjects);
            set({ projects });
        }
    },
    createNewProject: async (name, type, orgId) => {
        const { ProjectService } = await import('@/services/ProjectService');
        const { projectToMetadata } = await import('@/services/dashboard/projectTypeUtils');
        const newProject = await ProjectService.createProject(name, type, orgId);
        // Convert Project to ProjectMetadata at the boundary
        const metadata = projectToMetadata(newProject);
        set((state) => ({
            projects: [metadata, ...state.projects],
            currentProjectId: newProject.id,
            currentModule: type,
        }));
        return newProject.id;
    },
    updateProjectMetadata: async (projectId, metadata) => {
        const { ProjectService } = await import('@/services/ProjectService');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { projectToMetadata } = await import('@/services/dashboard/projectTypeUtils');
        
        // 1. Update in Firestore via ProjectService
        // We need to assume ProjectService has an update method or use patch
        // Let's check ProjectService after this.
        await ProjectService.update(projectId, { metadata });

        // 2. Update local state
        set((state) => ({
            projects: state.projects.map((p) => 
                p.id === projectId 
                    ? { ...p, metadata: { ...(p.metadata || {}), ...metadata } }
                    : p
            )
        }));
        
        
        logger.info(`[AppSlice] Updated metadata for project ${projectId}`);
    },
    updateProjectName: async (projectId, name) => {
        const { ProjectService } = await import('@/services/ProjectService');
        await ProjectService.updateProject(projectId, { name });

        set((state) => ({
            projects: state.projects.map((p) =>
                p.id === projectId ? { ...p, name } : p
            )
        }));
    },
    setProjectStatus: async (projectId, status) => {
        const { ProjectService } = await import('@/services/ProjectService');
        await ProjectService.setStatus(projectId, status);

        set((state) => ({
            projects: state.projects.map((p) =>
                p.id === projectId ? { ...p, status } : p
            )
        }));
    },
    deleteProject: async (projectId) => {
        const { ProjectService } = await import('@/services/ProjectService');
        // Actually we just archive it to be safe, or delete if wanted.
        await ProjectService.deleteProject(projectId);

        set((state) => ({
            projects: state.projects.filter((p) => p.id !== projectId),
            currentProjectId: state.currentProjectId === projectId ? DEFAULT_PROJECT_ID : state.currentProjectId
        }));
    },
    pendingPrompt: null,
    setPendingPrompt: (prompt) => set({ pendingPrompt: prompt }),
    apiKeyError: false,
    setApiKeyError: (error) => set({ apiKeyError: error }),
    isSidebarOpen: typeof window !== 'undefined' ? localStorage.getItem('indii_sidebarOpen') !== 'false' : true,
    isRightPanelOpen: typeof window !== 'undefined' ? localStorage.getItem('indii_rightPanelOpen') === 'true' : false,
    rightPanelTab: 'context',
    toggleSidebar: () => {
        const now = Date.now();
        const state = get();
        if (state._lastSidebarToggle && now - state._lastSidebarToggle < 200) {
            return; // Ignore rapid-fire toggles
        }
        const newState = !state.isSidebarOpen;
        if (typeof window !== 'undefined') {
            localStorage.setItem('indii_sidebarOpen', String(newState));
        }
        set({ isSidebarOpen: newState, _lastSidebarToggle: now });
    },
    toggleRightPanel: () => {
        // BUG-006 FIX: Debounce rapid toggle clicks.
        // The AnimatePresence mode="wait" in RightPanel can get stuck
        // if toggled faster than the spring animation duration (~100ms).
        const now = Date.now();
        const state = get();
        if (state._lastRightPanelToggle && now - state._lastRightPanelToggle < 100) {
            return; // Ignore rapid-fire toggles
        }
        const newState = !state.isRightPanelOpen;
        if (typeof window !== 'undefined') {
            localStorage.setItem('indii_rightPanelOpen', String(newState));
        }
        set({ isRightPanelOpen: newState, _lastRightPanelToggle: now });
    },
    setRightPanelTab: (tab) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('indii_rightPanelOpen', 'true');
        }
        set({ rightPanelTab: tab, isRightPanelOpen: true });
    },
    isCommandMenuOpen: false,
    setCommandMenuOpen: (open) => set({ isCommandMenuOpen: open }),
});
